import { Injectable, inject } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { ApiClientService } from '../../core/api-client.service';
import { I18nService } from '../../core/i18n.service';
import { SiteConfigService } from '../../core/site-config.service';

export interface SupportCategory {
  id: number;
  name: string;
  title: string;
  slug: string;
  parentID: number | null;
  imageUrl: string | null;
  hasChildren: boolean;
}

export interface SupportFaq {
  id: number;
  title: string;
  content: string;
}

export interface SupportMethod {
  id: number;
  title: string;
  description: string;
  type: string;
  createForm?: SupportFormField[];
}

export interface SupportFormField {
  id: number;
  formKey: string;
  type: string;
  title: string;
  required: boolean;
  minlength?: number | null;
  maxlength?: number | null;
}

export interface SupportCategoryPayload {
  category: SupportCategory | null;
  categories: SupportCategory[];
  subCategories: SupportCategory[];
  faqs: SupportFaq[];
  helpText: string | null;
  methods: SupportMethod[];
}

export interface SupportTicket {
  id: number;
  slug: string;
  title: string;
  priority: string;
  status: string;
  createdAt?: string | null;
  updatedAt?: string | null;
}

export interface SupportTicketMessage {
  id: number;
  message: string;
  createdAt?: string | null;
  senderLabel: string;
  isYou: boolean;
}

export interface SupportTicketDetails {
  id: number;
  slug: string;
  priority: string;
  status: string;
  createdAt?: string | null;
  messages: SupportTicketMessage[];
}

@Injectable({ providedIn: 'root' })
export class SupportService {
  private readonly api = inject(ApiClientService);
  private readonly i18n = inject(I18nService);
  private readonly site = inject(SiteConfigService);

  async center(): Promise<SupportCategoryPayload> {
    await this.site.ready();
    const response = await firstValueFrom(this.api.post<unknown>('supportCenter', { id: 'supportCenter', ...this.localePayload() }));
    const payload = this.extractPayload(response, 'supportCenter') ?? {};
    return {
      category: this.normalizeCategory(payload['selectedCategory']),
      categories: this.normalizeCategories(payload['categories']),
      subCategories: this.normalizeCategories(payload['subCategories']),
      faqs: this.normalizeFaqs(payload['faqs']),
      helpText: typeof payload['helpText'] === 'string' ? payload['helpText'] : null,
      methods: this.normalizeMethods(payload['methods'])
    };
  }

  async category(slug: string): Promise<SupportCategoryPayload> {
    await this.site.ready();
    const response = await firstValueFrom(this.api.post<unknown>('supportCategory', {
      slug,
      responseKey: 'supportCategory',
      ...this.localePayload()
    }));
    const payload = this.extractPayload(response, 'supportCategory') ?? {};
    return {
      category: this.normalizeCategory(payload['category']),
      categories: [],
      subCategories: this.normalizeCategories(payload['subCategories']),
      faqs: this.normalizeFaqs(payload['faqs']),
      helpText: typeof payload['helpText'] === 'string' ? payload['helpText'] : null,
      methods: this.normalizeMethods(payload['methods'])
    };
  }

  async tickets(): Promise<SupportTicket[]> {
    await this.site.ready();
    const response = await firstValueFrom(this.api.post<unknown>('supportTickets', {
      page: 0,
      limit: 30,
      ...this.localePayload()
    }));
    const data = this.extractData(response);
    return this.normalizeTickets(data?.['tickets']);
  }

  async ticketDetails(slug: string): Promise<{ ticketDetails: SupportTicketDetails | null; ticketFormDatas: unknown[] }> {
    await this.site.ready();
    const response = await firstValueFrom(this.api.post<unknown>('supportTicketDetails', { slug, ...this.localePayload() }));
    const data = this.extractData(response);
    const payload = data?.['supportTicketDetails'] as Record<string, unknown> | undefined;
    return {
      ticketDetails: this.normalizeTicketDetails(payload?.['ticketDetails']),
      ticketFormDatas: Array.isArray(payload?.['ticketFormDatas']) ? payload['ticketFormDatas'] as unknown[] : []
    };
  }

  async sendMessage(ticketID: number, message: string): Promise<void> {
    await this.site.ready();
    await firstValueFrom(this.api.post<unknown>('sendTicketMessage', {
      ticketID,
      message,
      ...this.localePayload()
    }));
  }

  async method(ticketMethodID: number): Promise<{ ticketMethodID: number; formFields: SupportFormField[] }> {
    await this.site.ready();
    const response = await firstValueFrom(this.api.post<unknown>('supportMethod', {
      ticketMethodID,
      ...this.localePayload()
    }));
    const payload = this.extractPayload(response, 'supportMethod') ?? {};
    return {
      ticketMethodID: Number(payload['ticketMethodID'] ?? ticketMethodID),
      formFields: this.normalizeFormFields(payload['formFields'])
    };
  }

  async createTicket(methodID: number, values: Record<string, unknown>, categoryID = 0): Promise<{ ticketID: number; slug: string | null }> {
    await this.site.ready();
    const response = await firstValueFrom(this.api.post<unknown>('createTicket', {
      id: methodID,
      methodID,
      categoryID,
      values,
      ...this.localePayload()
    }));
    const data = this.extractData(response);
    const created = data?.['createTicket'];
    if (!created || typeof created !== 'object' || Number((created as Record<string, unknown>)['ticketID'] ?? 0) <= 0) {
      throw new Error(this.extractErrorMessage(response) || 'Destek talebi oluşturulamadı.');
    }
    const item = created as Record<string, unknown>;
    return { ticketID: Number(item['ticketID']), slug: item['slug'] ? String(item['slug']) : null };
  }

  private extractErrorMessage(value: unknown): string {
    if (!value || typeof value !== 'object') return '';
    if (Array.isArray(value)) {
      for (const item of value) {
        const found = this.extractErrorMessage(item);
        if (found) return found;
      }
      return '';
    }
    const row = value as Record<string, unknown>;
    if (typeof row['message'] === 'string' && row['message'].trim() && row['message'] !== 'Operation failed') return row['message'].trim();
    if (typeof row['alertMessage'] === 'string' && row['alertMessage'].trim()) return row['alertMessage'].trim();
    for (const child of Object.values(row)) {
      const found = this.extractErrorMessage(child);
      if (found) return found;
    }
    return '';
  }

  private localePayload(): { lang: string; country: string; locale: string } {
    return {
      lang: this.i18n.lang(),
      country: this.i18n.country(),
      locale: this.i18n.locale()
    };
  }

  private extractPayload(response: unknown, key: string): Record<string, unknown> | null {
    const data = this.extractData(response);
    const direct = data?.[key];
    if (direct && typeof direct === 'object') {
      return direct as Record<string, unknown>;
    }
    return null;
  }

  private extractData(response: unknown): Record<string, unknown> | null {
    const roots = Array.isArray(response) ? response : [response];
    for (const rootValue of roots) {
      const root = rootValue as Record<string, unknown>;
      if (root?.['data'] && typeof root['data'] === 'object') {
        return root['data'] as Record<string, unknown>;
      }
      if (root?.['apiData'] && typeof root['apiData'] === 'object') {
        return root['apiData'] as Record<string, unknown>;
      }
    }
    return null;
  }

  private normalizeCategories(value: unknown): SupportCategory[] {
    if (!Array.isArray(value)) {
      return [];
    }
    return value.map((item) => this.normalizeCategory(item)).filter((item): item is SupportCategory => Boolean(item));
  }

  private normalizeCategory(value: unknown): SupportCategory | null {
    if (!value || typeof value !== 'object') {
      return null;
    }
    const item = value as Record<string, unknown>;
    return {
      id: Number(item['id'] ?? 0),
      name: String(item['name'] ?? item['title'] ?? 'Destek'),
      title: String(item['title'] ?? item['name'] ?? 'Destek'),
      slug: String(item['slug'] ?? item['id'] ?? ''),
      parentID: item['parentID'] ? Number(item['parentID']) : null,
      imageUrl: item['imageUrl'] ? String(item['imageUrl']) : null,
      hasChildren: Boolean(item['hasChildren'])
    };
  }

  private normalizeFaqs(value: unknown): SupportFaq[] {
    if (!Array.isArray(value)) {
      return [];
    }
    return value.map((item: Record<string, unknown>, index) => ({
      id: Number(item['id'] ?? item['faqID'] ?? index + 1),
      title: String(item['title'] ?? item['question'] ?? 'Soru'),
      content: String(item['content'] ?? item['answer'] ?? '')
    }));
  }

  private normalizeMethods(value: unknown): SupportMethod[] {
    if (!Array.isArray(value)) {
      return [];
    }
    return value.map((item: Record<string, unknown>, index) => ({
      id: Number(item['id'] ?? index + 1),
      title: String(item['title'] ?? 'Destek talebi'),
      description: String(item['description'] ?? 'Destek ekibimiz bu kanaldan donus yapar.'),
      type: String(item['type'] ?? 'ticket'),
      createForm: this.normalizeFormFields(item['createForm'])
    }));
  }

  private normalizeFormFields(value: unknown): SupportFormField[] {
    if (!Array.isArray(value)) return [];
    return value.map((item: Record<string, unknown>) => ({
      id: Number(item['id'] ?? 0),
      formKey: String(item['formKey'] ?? `field_${item['id'] ?? 0}`),
      type: String(item['type'] ?? 'text').toLowerCase(),
      title: String(item['title'] ?? 'Bilgi'),
      required: Boolean(item['required']),
      minlength: (item['minlength'] ?? item['minlenght']) == null ? null : Number(item['minlength'] ?? item['minlenght']),
      maxlength: (item['maxlength'] ?? item['maxlenght']) == null ? null : Number(item['maxlength'] ?? item['maxlenght'])
    })).filter((field) => field.id > 0);
  }

  private normalizeTickets(value: unknown): SupportTicket[] {
    if (!Array.isArray(value)) {
      return [];
    }
    return value.map((item: Record<string, unknown>) => ({
      id: Number(item['id'] ?? 0),
      slug: String(item['slug'] ?? item['id'] ?? ''),
      title: String(item['title'] ?? `Talep #${item['id'] ?? ''}`),
      priority: String(item['priority'] ?? ''),
      status: String(item['status'] ?? ''),
      createdAt: item['createdAt'] ? String(item['createdAt']) : null,
      updatedAt: item['updatedAt'] ? String(item['updatedAt']) : null
    }));
  }

  private normalizeTicketDetails(value: unknown): SupportTicketDetails | null {
    if (!value || typeof value !== 'object') {
      return null;
    }
    const item = value as Record<string, unknown>;
    return {
      id: Number(item['id'] ?? 0),
      slug: String(item['slug'] ?? item['id'] ?? ''),
      priority: String(item['priority'] ?? ''),
      status: String(item['status'] ?? ''),
      createdAt: item['createdAt'] ? String(item['createdAt']) : null,
      messages: Array.isArray(item['messages'])
        ? item['messages'].map((message: Record<string, unknown>) => ({
          id: Number(message['id'] ?? 0),
          message: String(message['message'] ?? ''),
          createdAt: message['createdAt'] ? String(message['createdAt']) : null,
          senderLabel: String(message['senderLabel'] ?? message['name'] ?? 'Destek'),
          isYou: Boolean(message['isYou'])
        }))
        : []
    };
  }
}
